import { useContext, useEffect, useMemo, useState }              from "react";
import Link                                                      from "next/link";
import { useRouter }                                             from "next/router";
import { Trophy, Crown, Search, ChevronDown }                    from "lucide-react";
import Context                                                   from "@/provider/Context";
import RankingTable                                              from "@/components/RankingTable";
import Avatar                                                    from "@/components/Avatar";
import Footer                                                    from "@/components/common/Footer";
import { ordenar_por_posicao, ranking_da_categoria,
  link_do_perfil }                                               from "@/utils/MemberFunction";
import { resolver_categoria, link_da_categoria,
  cor_segura, com_alfa, formatar_valor, formatar_data,
  explicacao_da_unidade, categorias_do_catalogo,
  atividades_da_categoria }                                      from "@/utils/RpgFunction";

/* /rankings            -> Global (nivel e XP)
   /rankings/<chave>    -> uma categoria do catalogo (um Rei ativo)
   /rankings/pvp, /rankings/sea-events... (links antigos) -> a categoria nova que tem a atividade */

const POR_PAGINA = 50;

const normalizar = (texto) => String(texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function Rankings() {
  const { Jogadores, Reis, Catalogo, Carregado }                  = useContext(Context);
  const router                                                    = useRouter();
  const partes                                                    = router.query.slug || [];
  const slug                                                      = partes[0];

  const categorias                                                = categorias_do_catalogo(Catalogo);
  const pronto                                                    = router.isReady && (Carregado || !slug);
  const resolvida                                                 = (partes.length > 1) ? null : resolver_categoria(slug, categorias);
  const global                                                    = resolvida === "global";
  const categoria                                                 = (resolvida && !global) ? resolvida : null;
  const rei                                                       = categoria ? (Reis.find((r) => r?.chave === categoria.chave) || null) : null;
  const donoHref                                                  = link_do_perfil(rei?.dono, Jogadores);

  const [busca, set_busca]                                        = useState("");
  const [limite, set_limite]                                      = useState(POR_PAGINA);

  // trocou de aba: volta para o comeco da lista
  useEffect(() => { set_limite(POR_PAGINA); }, [slug]);

  const linhas = useMemo(() => {
    if (global) return ordenar_por_posicao(Jogadores).map((jogador) => ({ jogador, posicao: jogador.posicao ?? null, valor: jogador.xp }));
    if (categoria) return ranking_da_categoria(categoria.chave, Jogadores);
    return [];
  }, [global, categoria, Jogadores]);

  const filtradas = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return linhas;
    return linhas.filter(({ jogador }) => (
      normalizar(jogador?.nome).includes(termo) ||
      normalizar(jogador?.roblox?.username).includes(termo) ||
      normalizar(jogador?.crew?.nome).includes(termo)
    ));
  }, [linhas, busca]);

  const visiveis = filtradas.slice(0, limite);

  const abas = [
    { chave: null, nome: "Global", cor: null },
    ...categorias.map((c) => ({ chave: c.chave, nome: c.nome, cor: cor_segura(c.cor) })),
  ];

  const vazio = busca.trim()
    ? "Ninguém com esse nome neste ranking."
    : global ? "Nenhum jogador no ranking ainda." : "Ninguém pontuou nesta categoria ainda.";

  return (
    <div className="min-h-screen">
      <section className="pt-24 pb-8">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-4">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="text-sm font-rajdhani text-primary-foreground">
              Ranking do RPG
            </span>
          </div>

          <h1 className="font-orbitron text-4xl md:text-5xl font-black text-neon mb-3">
            RANKINGS
          </h1>

          <p className="text-muted-foreground font-rajdhani max-w-xl mx-auto">
            O Global ordena por nível e XP. Cada categoria é a disputa de um Rei.
            Aberto para todo mundo do servidor, da crew ou não.
          </p>
        </div>
      </section>

      {/* Abas */}
      <section className="pb-8 border-b border-border">
        <div className="container mx-auto px-4 flex flex-wrap justify-center gap-2">
          {abas.map((aba) => {
            const ativa = pronto && (aba.chave ? categoria?.chave === aba.chave : global);

            return (
              <Link
                key={aba.chave || "global"}
                href={link_da_categoria(aba.chave)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-rajdhani font-semibold transition-all ${
                  ativa
                    ? "bg-primary text-primary-foreground void-glow"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {aba.chave
                  ? <Crown className="w-4 h-4" style={ativa ? undefined : { color: aba.cor }} />
                  : <Trophy className="w-4 h-4" />}
                {aba.nome}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Sobre a categoria + busca */}
      <section className="py-6">
        <div className="container mx-auto px-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            {pronto && categoria && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-muted-foreground font-rajdhani">
                  {atividades_da_categoria(categoria, Catalogo)}
                  <span className="block">Esta categoria {explicacao_da_unidade(categoria.unidade)}.</span>
                </p>

                {rei?.dono && (() => {
                  const desde    = formatar_data(rei.dono.desde);
                  const conteudo = (
                    <>
                      <Avatar src={rei.dono.avatar} alt={rei.dono.nome} className="w-8 h-8 rounded-md object-cover" />
                      <div className="text-left">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Crown className="w-3 h-3" style={{ color: cor_segura(categoria.cor) }} />
                          {categoria.nome} atual{desde ? ` desde ${desde}` : ""}
                        </p>
                        <p className="text-sm font-rajdhani font-semibold">
                          {rei.dono.nome} · {formatar_valor(rei.dono.valor, categoria.unidade)}
                        </p>
                      </div>
                    </>
                  );
                  const classe = "flex items-center gap-2 px-3 py-2 rounded-lg border flex-shrink-0";
                  const estilo = { borderColor: com_alfa(categoria.cor, 0.5), backgroundColor: com_alfa(categoria.cor, 0.1) };

                  return donoHref
                    ? <Link href={donoHref} className={`${classe} hover:brightness-125 transition`} style={estilo}>{conteudo}</Link>
                    : <div className={classe} style={estilo}>{conteudo}</div>;
                })()}
              </div>
            )}
            {pronto && global && (
              <p className="text-sm text-muted-foreground font-rajdhani">
                Posição por XP total. As coroas ao lado do nome são os Reis que o jogador segura agora.
              </p>
            )}
          </div>

          <div className="relative w-full lg:w-80 flex-shrink-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar jogador ou crew..."
              value={busca}
              onChange={(e) => { set_busca(e.target.value); set_limite(POR_PAGINA); }}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-secondary border border-border font-rajdhani placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
            />
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4">
          {!pronto ? (
            <div className="void-card p-8 text-center text-muted-foreground font-rajdhani">Carregando ranking...</div>
          ) : !resolvida ? (
            <div className="void-card p-8 text-center">
              <p className="font-orbitron text-xl text-muted-foreground mb-4">Categoria não encontrada</p>
              <Link href="/rankings" className="text-primary hover:underline font-rajdhani">Ver o ranking Global</Link>
            </div>
          ) : (
            <>
              <RankingTable
                linhas={visiveis}
                categoria={categoria}
                donoId={rei?.dono?.discordId || null}
                titulo={global ? "Global · Nível e XP" : categoria.nome}
                vazio={Carregado ? vazio : "Carregando ranking..."}
              />

              {filtradas.length > limite && (
                <div className="text-center mt-6">
                  <button
                    type="button"
                    onClick={() => set_limite((atual) => atual + POR_PAGINA)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary/50 font-rajdhani font-semibold hover:bg-primary/10 hover:border-primary transition-all"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Mostrar mais ({filtradas.length - limite} restantes)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
